import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getModalData } from '../../modules/selectors';
import { getModalsStates } from '../../modules/selectors/modals';
import { changeModalState } from '../../modules/actions/modals';
import {
  cancelSubscription,
  addPromotionCodeToSubscription
} from '../../modules/actions/subscription';
import { radioMap } from './FirstStep/data';
import history from '../../helpers/history';
import { SubscriptionType } from '../../modules/types/subscriptions';
import { getPaymentInfo } from '../../modules/selectors/paymentInfo';
import moment from 'moment';
import { getUser } from '../../modules/selectors/user';
import queryString from 'query-string';

interface IStripePromotionCodeMap {
  [key: string]: {
    [key: string]: string;
  };
}

const stripePromotionCodeMap: IStripePromotionCodeMap = {
  production: {
    yearly: 'BHJjIfiB',
    monthly: 'KYbHP4KL'
  },
  development: {
    yearly: 'h8RcHiub',
    monthly: 'DKWLp9Oh'
  },
  staging: {
    yearly: '42WxwZYh',
    monthly: 'ZqqqCwwH'
  }
};

interface IRadioQuestionMap {
  [key: string]: {
    [key: string]: string;
  };
}

const radioQuestionMap: IRadioQuestionMap = {
  'not-using-product-anymore': {
    firstQuestion: 'why-stop-using',
    secondQuestion: 'problems-using-product'
  },
  price: {
    firstQuestion: 'what-you-expected',
    secondQuestion: 'what-price'
  },
  'switching-to-another-product': {
    firstQuestion: 'what-another-product',
    secondQuestion: 'why-another-product'
  },
  'missing-specific-function': {
    firstQuestion: 'missing-functionality',
    secondQuestion: 'function-not-working-as-expected'
  },
  'customers-have-issues': {
    firstQuestion: 'customers-problems'
  },
  'short-term': {
    firstQuestion: 'were-you-sutisfied',
    secondQuestion: 'will-you-return'
  }
};

const useModalHelpers = () => {
  const dispatch = useDispatch();
  const modalData = useSelector(getModalData);
  const {
    activeRadio = '',
    subscriptionType = '',
    firstAnswer = '',
    secondAnswer = '',
    additionalAnswer = ''
  } = modalData;
  const { subscriptionCancelModal: isOpen } = useSelector(getModalsStates);
  const { paymentProvider, subscriptions } = useSelector(getPaymentInfo);
  const { subscription = {} } = useSelector(getUser);
  const parsedQueryString = queryString.parse(history?.location?.search);

  const coreSubscription = subscriptions.find((s) => s.subscriptionType === 'core');
  const defaultSubscription = subscriptions.some((s) => s.subscriptionType === '');

  const [isChurnOfferDiscountModalOpen, setIsChurnOfferDiscountModalOpen] = useState(false);
  const [wasChurnOfferDicountModalOpen, setWasChurnOfferDicountModalOpen] = useState(false);

  useEffect(() => {
    if (
      !!parsedQueryString?.openChurnOfferDiscountModal &&
      !defaultSubscription &&
      !coreSubscription
    ) {
      history.push('subscription/pricing');
    } else if (
      !!parsedQueryString?.openChurnOfferDiscountModal &&
      !!coreSubscription &&
      !isChurnOfferDiscountModalOpen &&
      !wasChurnOfferDicountModalOpen
    ) {
      setWasChurnOfferDicountModalOpen(true);
      setIsChurnOfferDiscountModalOpen(true);
    }
  }, [parsedQueryString, coreSubscription, defaultSubscription]); // eslint-disable-line

  const showChurnOfferDiscount =
    !coreSubscription?.acceptedChurnDiscountOfferAt ||
    moment(coreSubscription.acceptedChurnDiscountOfferAt).isBefore(moment().subtract(13, 'month'));

  const coreSubscriptionIntervall = (subscription?.intervall || '') as string;

  const [step, setStep] = useState<number>(1);

  const [isApplyingOffer, setisApplyingOffer] = useState(false);

  const closeModal = () => {
    if (isChurnOfferDiscountModalOpen) {
      setIsChurnOfferDiscountModalOpen(false);

      return;
    }

    dispatch(
      changeModalState({
        key: 'subscriptionCancelModal',
        state: false,
        modalData: {
          activeRadio: '',
          firstAnswer: '',
          secondAnswer: '',
          additionalAnswer: ''
        }
      })
    );
    setStep(1);
  };

  const goNextStep = () => {
    const isRandomlyHere = activeRadio === radioMap.firstRadio;
    const isLastStep =
      step ===
      (modalData?.subscriptionType === 'core' &&
      paymentProvider === 'stripe' &&
      showChurnOfferDiscount
        ? 4
        : 3);

    if (isRandomlyHere) {
      closeModal();

      return history.push('/subscription/pricing');
    }

    if (isLastStep) {
      const isOneQuestion = activeRadio === radioMap.sixthRadio;

      const finalAnswer = [
        { questionId: 'cancel-reason', answerId: activeRadio },
        { questionId: radioQuestionMap[activeRadio].firstQuestion, answer: firstAnswer },
        { questionId: radioQuestionMap[activeRadio].secondQuestion, answer: secondAnswer },
        { questionId: 'other-feedback', answer: additionalAnswer }
      ];

      const finalOneAnswer = [
        { questionId: 'cancel-reason', answerId: activeRadio },
        { questionId: radioQuestionMap[activeRadio].firstQuestion, answer: firstAnswer },
        { questionId: 'other-feedback', answer: additionalAnswer }
      ];

      dispatch(
        cancelSubscription({
          subscriptionType,
          questionnaireAnswers: isOneQuestion ? finalOneAnswer : finalAnswer
        })
      );

      return closeModal();
    }

    setStep((prev: number) => prev + 1);
  };

  const handleValue = (name: string, value: string) => {
    dispatch(
      changeModalState({
        key: 'subscriptionCancelModal',
        state: true,
        modalData: { ...modalData, [name]: value }
      })
    );
  };

  const openModal = useCallback((subscriptionType: SubscriptionType) => {
    dispatch(
      changeModalState({
        key: 'subscriptionCancelModal',
        state: true,
        modalData: { ...modalData, subscriptionType }
      })
    );
    // eslint-disable-next-line
  }, []);

  const claimOffer = () => {
    const promotionCodeId =
      stripePromotionCodeMap[process.env.REACT_APP_ENVIRONMENT][coreSubscriptionIntervall];

    dispatch(addPromotionCodeToSubscription(promotionCodeId));
    setisApplyingOffer(true);
  };

  return {
    step,
    isOpen,
    isChurnOfferDiscountModalOpen,
    goNextStep,
    handleValue,
    openModal,
    closeModal,
    activeRadio,
    claimOffer,
    isApplyingOffer
  };
};

export default useModalHelpers;
